import React, { useState } from 'react';
import { useTheme } from '../theme/ThemeContext';
import { ColorScheme } from '../theme/theme';

interface ThemeSettingsProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function ThemeSettings({ isOpen, onClose }: ThemeSettingsProps) {
  const { mode, colorScheme, toggleMode, setColorScheme } = useTheme();

  const colorSchemes: { key: ColorScheme; name: string; color: string }[] = [
    { key: 'violet', name: 'LexHR', color: '#8b5cf6' },
    { key: 'blue', name: 'Blue', color: '#3b82f6' },
    { key: 'indigo', name: 'Indigo', color: '#6366f1' },
    { key: 'purple', name: 'Purple', color: '#a855f7' },
    { key: 'green', name: 'Green', color: '#22c55e' },
    { key: 'orange', name: 'Orange', color: '#f97316' },
  ];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[200]">
      <div className="theme-bg rounded-lg shadow-xl p-6 w-full max-w-xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold theme-text">Theme Settings</h2>
          <button
            onClick={onClose}
            className="theme-text-muted hover:theme-text transition-colors p-1 rounded-full hover:theme-bg-secondary"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Dark/Light Mode Toggle */}
        <div className="mb-6">
          <h3 className="text-sm font-medium theme-text mb-3">Appearance</h3>
          <div className="flex items-center space-x-3">
            <button
              onClick={toggleMode}
              className={`flex items-center space-x-2 px-3 py-2 rounded-lg transition-colors ${
                mode === 'light' 
                  ? 'theme-primary theme-text-inverse' 
                  : 'theme-bg-secondary theme-text-secondary hover:theme-bg-tertiary'
              }`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
              <span className="text-sm">Light</span>
            </button>
            <button
              onClick={toggleMode}
              className={`flex items-center space-x-2 px-3 py-2 rounded-lg transition-colors ${
                mode === 'dark' 
                  ? 'theme-primary theme-text-inverse' 
                  : 'theme-bg-secondary theme-text-secondary hover:theme-bg-tertiary'
              }`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
              </svg>
              <span className="text-sm">Dark</span>
            </button>
          </div>
        </div>

        {/* Color Scheme Selection */}
        <div className="mb-6">
          <h3 className="text-sm font-medium theme-text mb-3">Color Scheme</h3>
          <div className="grid grid-cols-6 gap-2">
            {colorSchemes.map((scheme) => (
              <button
                key={scheme.key}
                onClick={() => setColorScheme(scheme.key)}
                className={`flex flex-col items-center p-3 rounded-lg transition-all hover:theme-bg-secondary ${
                  colorScheme === scheme.key 
                    ? 'ring-2 ring-offset-2 theme-bg-secondary' 
                    : ''
                }`}
                style={{
                  ...(colorScheme === scheme.key && {
                    '--tw-ring-color': scheme.color
                  } as React.CSSProperties)
                }}
              >
                <div
                  className="w-6 h-6 rounded-full mb-1"
                  style={{ backgroundColor: scheme.color }}
                />
                <span className="text-xs theme-text-secondary">{scheme.name}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Preview */}
        <div className="theme-bg-secondary rounded-lg p-4">
          <h4 className="text-sm font-medium theme-text mb-2">Preview</h4>
          <div className="space-y-2">
            <div className="flex items-center space-x-2">
              <div className="theme-primary w-3 h-3 rounded-full"></div>
              <span className="text-sm theme-text">Primary color</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="theme-secondary w-3 h-3 rounded-full"></div>
              <span className="text-sm theme-text-secondary">Secondary text</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="theme-sidebar-bg w-3 h-3 rounded-full"></div>
              <span className="text-sm theme-text-muted">Sidebar background</span>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-6 flex justify-end">
          <button
            onClick={onClose}
            className="theme-primary theme-text-inverse px-4 py-2 rounded-lg hover:theme-primary-hover transition-colors"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
